import { useErrorHandler } from '@app/core/hooks/useErrorHandler';
import { queryClient } from '@app/core/integrations/react-query/react-query';
import { useToast } from '@app/core/notifications';
import { composeRouteGenerator } from '@app/modules/compose/routing/compose-route-generator';
import { webApi } from '@mi-front/api-clients';
import { useRouter } from '@mi-front/core';
import { defineMessage, useIntl } from 'react-intl';
import { Station } from '@app/core/types/stations';

const composeCreateSuccessToastMessage = defineMessage({
  id: 'compose.createPage.successToast',
  defaultMessage: 'Composición creada',
});

/**
 * Submits a new composition for the given station, refreshes the station list cache,
 * and navigates to the new composition detail.
 */
export function useComposeCreateComposition(station: Station) {
  const { history } = useRouter();
  const { $t } = useIntl();
  const { notifySuccess } = useToast();
  const { onApiError } = useErrorHandler();
  const listId = `compose-compositions-list-${station.slug}`;

  const mutation = webApi.useCreateCompositionsDetail();

  async function submitCreate(name: string) {
    try {
      const composition = await mutation.mutateAsync({
        data: { name: name.trim(), stationId: station.id },
      });

      await queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === listId,
      });

      notifySuccess($t(composeCreateSuccessToastMessage));
      history.push(composeRouteGenerator.view(composition.id));
    } catch (error) {
      onApiError(error);
    }
  }

  return {
    submitCreate,
    isSubmitting: mutation.isPending,
  };
}
